import { UserStateStorage } from "@/lib/tour/persistent-storage/userState";
import { GuideRegistry } from "@/stores/GuideRegistry";
import { Checklist } from "@common/schemas/checklist";
import { ChevronLeftIcon, CircleCheckIcon, ListTodoIcon, PlayIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import { MinimizedAvatar } from "@/components/MinimizedAvatar";
import { AvatarTooltip } from "@/components/AvatarTooltip";
import { AUDIO_ONLY_MINIMIZED_AVATAR_IMG } from "@/lib/tour/tourPlayback/tourPlaybackManager";
import { LogoBlack } from "../LogoBlack";

type ChecklistCardWrapperProps = {
  checklist: Checklist;
  userStateStorage: UserStateStorage;
  guideRegistry: GuideRegistry;
  onTourStartClicked: (guideId: string) => void;
  onMinimize?: () => void;
  onExpand?: () => void;
  isMinimized?: boolean;
  onWatermarkClick?: () => void;
  onDismiss?: () => void;
};

export const ChecklistCardWrapper = observer(
  ({
    checklist,
    userStateStorage,
    guideRegistry,
    onTourStartClicked,
    onMinimize,
    onExpand,
    isMinimized,
    onDismiss,
  }: ChecklistCardWrapperProps) => {
    const previewImageUrls = guideRegistry.getPreviewImageUrls(checklist.items.map((item) => item.guide_id));
    const unfinishedGuideIds = checklist.items.filter(
      (item) => !userStateStorage.finishedTourGuideIds.includes(item.guide_id)
    );
    const totalGuideLengthInMs = guideRegistry.getTotalGuideLengthInMs(unfinishedGuideIds.map((item) => item.guide_id));
    const totalGuideLengthInMinutes = totalGuideLengthInMs / 60000;
    return (
      <ChecklistCard
        checklist={checklist}
        finishedGuideIds={userStateStorage.finishedTourGuideIds}
        previewImageUrls={previewImageUrls}
        totalGuideLengthInMinutes={totalGuideLengthInMinutes}
        onTourStartClicked={onTourStartClicked}
        onMinimize={onMinimize}
        onExpand={onExpand}
        onDismiss={onDismiss}
        isMinimized={isMinimized}
      />
    );
  }
);

type ChecklistCardProps = {
  checklist: Checklist;
  finishedGuideIds: string[];
  previewImageUrls: Record<string, string>;
  totalGuideLengthInMinutes: number;
  onTourStartClicked: (guideId: string) => void;
  onMinimize?: () => void;
  onExpand?: () => void;
  isMinimized?: boolean;
  onWatermarkClick?: () => void;
  onDismiss?: () => void;
};

export const ChecklistCard = observer(
  ({
    checklist,
    finishedGuideIds = [],
    previewImageUrls,
    totalGuideLengthInMinutes,
    onTourStartClicked,
    onMinimize,
    onExpand,
    isMinimized,
    onWatermarkClick,
    onDismiss,
  }: ChecklistCardProps) => {
    // Initialize expanded items with unfinished items
    const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
      if (!checklist) return new Set();

      const unfinishedItemIds = checklist.items
        .filter((item) => !finishedGuideIds.includes(item.guide_id))
        .map((item) => item.id);

      return new Set(unfinishedItemIds);
    });

    // Recalculate expanded states when finishedGuideIds changes
    useEffect(() => {
      if (!checklist) return;

      const unfinishedItemIds = checklist.items
        .filter((item) => !finishedGuideIds.includes(item.guide_id))
        .map((item) => item.id);

      setExpandedItems(new Set(unfinishedItemIds));
    }, [finishedGuideIds, checklist]);

    if (!checklist) return null;

    const totalItems = checklist.items.length;
    const completedItems = checklist.items.filter((item) => finishedGuideIds.includes(item.guide_id));
    const completedCount = completedItems.length;
    const language = checklist.settings?.language?.code ?? "en";
    const translations = translationsMap[language];

    const toggleExpanded = (itemId: string) => {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      setExpandedItems(newExpanded);
    };

    const defaultImageIfEmpty = (imageUrl: string) => {
      if (!imageUrl || imageUrl === "") {
        return AUDIO_ONLY_MINIMIZED_AVATAR_IMG;
      }
      return imageUrl;
    };

    if (isMinimized) {
      return (
        <div
          id="pyne-minimized-widget-wrapper"
          style={{
            position: "relative",
            zIndex: "var(--pyne-layer-avatar)",
            opacity: isMinimized ? 1 : 0,
            transition: "opacity 0.2s ease-out",
          }}
        >
          {isMinimized && (
            <MinimizedAvatar onDismiss={onDismiss}>
              <AvatarTooltip content={checklist.title ?? checklist.name} tooltipProps={{ placement: "right" }}>
                <div className="pyne-minimized-avatar__button" onClick={onExpand}>
                  <div className="pyne-minimized-avatar__checklist-button">
                    <ListTodoIcon />
                  </div>
                </div>
              </AvatarTooltip>
            </MinimizedAvatar>
          )}
        </div>
      );
    }

    return (
      <div className="pyne-checklist-container">
        <div
          className="pyne-checklist"
          style={{
            position: "relative",
            maxHeight: "calc(100vh - 280px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="pyne-checklist__header">
            <h3 className="pyne-checklist__title">{checklist.title ?? checklist.name}</h3>
            <p className="pyne-checklist__subtitle">{checklist.description}</p>

            <div className="pyne-checklist__progress">
              <span className="pyne-checklist__progress-text">
                {completedCount} {translations.of} {totalItems}
              </span>
              {/* <span className="pyne-checklist__time-left">
                {totalGuideLengthInMinutes > 0
                  ? totalGuideLengthInMinutes < 1
                    ? `${Math.round(totalGuideLengthInMinutes * 60)} ${translations.secondsLeft}`
                    : `${totalGuideLengthInMinutes.toFixed(1)} ${translations.minutesLeft}`
                  : translations.allCompleted}
              </span> */}
            </div>

            <div className="pyne-checklist__progress-bar">
              <div
                className="pyne-checklist__progress-fill"
                style={{ width: `${totalItems > 0 ? (completedCount / totalItems) * 100 : 0}%` }}
              />
            </div>
          </div>

          <div className="pyne-checklist__items">
            {checklist.items.map((item) => {
              const isCompleted = finishedGuideIds.includes(item.guide_id);
              const isExpanded = expandedItems.has(item.id);

              return (
                <div
                  key={item.id}
                  className={`pyne-checklist__item ${isCompleted ? "pyne-checklist__item--completed" : ""} ${isExpanded ? "pyne-checklist__item--expanded" : ""}`}
                >
                  <div className="pyne-checklist__item-checkbox">
                    {isCompleted ? (
                      <div className="pyne-checklist__checkbox pyne-checklist__checkbox--checked">
                        <CircleCheckIcon />
                      </div>
                    ) : (
                      <div className="pyne-checklist__checkbox pyne-checklist__checkbox--unchecked" />
                    )}
                  </div>

                  <div className="pyne-checklist__item-content">
                    <p className="pyne-checklist__item-title">{item.name}</p>

                    {isExpanded && (
                      <div className="pyne-checklist__item-expanded-content">
                        {item.description && <p className="pyne-checklist__item-description">{item.description}</p>}
                        <button
                          className="pyne-checklist__item-button"
                          onClick={() => onTourStartClicked(item.guide_id)}
                        >
                          <PlayIcon size={16} />
                          <span>{translations.viewVideoGuide}</span>
                          <div className="pyne-checklist__button-avatar">
                            <img
                              className="pyne-checklist__avatar-img"
                              src={defaultImageIfEmpty(previewImageUrls[item.guide_id])}
                              alt={item.name}
                            />
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="pyne-checklist__item-actions">
                    <button
                      className={`pyne-checklist__expand-btn ${isExpanded ? "pyne-checklist__expand-btn--expanded" : ""}`}
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              marginTop: 16,
              marginBottom: -16,
            }}
          >
            <div
              onClick={() => onWatermarkClick?.()}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "#000" }}>{translations.shareFeedback} — </span>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <LogoBlack style={{ width: 48, height: 16 }} />
              </div>
            </div>
          </div> */}
        </div>
        <div
          id="pyne-minimized-widget-wrapper"
          style={{
            position: "relative",
            zIndex: "var(--pyne-layer-avatar)",
            opacity: 1,
            transition: "opacity 0.2s ease-out",
          }}
        >
          <MinimizedAvatar disableDismiss>
            <AvatarTooltip content={translations.hide} tooltipProps={{ placement: "right" }}>
              <div className="pyne-minimized-avatar__button" onClick={onMinimize}>
                <div className="pyne-minimized-avatar__checklist-button">
                  <ChevronLeftIcon className="pyne-chevron-left-icon" />
                </div>
              </div>
            </AvatarTooltip>
          </MinimizedAvatar>
        </div>
      </div>
    );
  }
);

const translationsMap: Record<string, Record<string, string>> = {
  en: {
    of: "of",
    secondsLeft: "seconds left",
    minutesLeft: "minutes left",
    allCompleted: "All completed",
    viewVideoGuide: "View video guide",
    shareFeedback: "Share feedback",
    hide: "Hide",
  },
  de: {
    of: "von",
    secondsLeft: "Sekunden übrig",
    minutesLeft: "Minuten übrig",
    allCompleted: "Alle abgeschlossen",
    viewVideoGuide: "Video-Guide ansehen",
    shareFeedback: "Feedback teilen",
    hide: "Ausblenden",
  },
  tr: {
    of: "/",
    secondsLeft: "saniye kaldı",
    minutesLeft: "dakika kaldı",
    allCompleted: "Hepsi tamamlandı",
    viewVideoGuide: "Video kılavuzunu görüntüle",
    shareFeedback: "Geri bildirim paylaş",
    hide: "Gizle",
  },
  fr: {
    of: "sur",
    secondsLeft: "secondes restantes",
    minutesLeft: "minutes restantes",
    allCompleted: "Tout terminé",
    viewVideoGuide: "Voir le guide vidéo",
    shareFeedback: "Partager des commentaires",
    hide: "Masquer",
  },
  it: {
    of: "di",
    secondsLeft: "secondi rimasti",
    minutesLeft: "minuti rimasti",
    allCompleted: "Tutto completato",
    viewVideoGuide: "Visualizza guida video",
    shareFeedback: "Condividi feedback",
    hide: "Nascondi",
  },
  ua: {
    of: "з",
    secondsLeft: "секунд залишилося",
    minutesLeft: "хвилин залишилося",
    allCompleted: "Усе завершено",
    viewVideoGuide: "Переглянути відео-посібник",
    shareFeedback: "Поділитися відгуком",
    hide: "Сховати",
  },
  es: {
    of: "de",
    secondsLeft: "segundos restantes",
    minutesLeft: "minutos restantes",
    allCompleted: "Todo completado",
    viewVideoGuide: "Ver guía de video",
    shareFeedback: "Compartir comentarios",
    hide: "Ocultar",
  },
  ar: {
    of: "من",
    secondsLeft: "ثانية متبقية",
    minutesLeft: "دقائق متبقية",
    allCompleted: "اكتمل الكل",
    viewVideoGuide: "عرض دليل الفيديو",
    shareFeedback: "مشاركة التعليقات",
    hide: "إخفاء",
  },
};
